'use client';

import { useMemo, useRef, useCallback, useState } from "react";
import { useParams } from "next/navigation";
import { toast } from "react-toastify";

// Ctx
import useListListingCtx from "@src/context/list-listing-office-ctx";
import SkeletonListListing from "@src/components/atoms/skeleton-list-listing";
import useGlobalCtx from "@src/context/global-ctx";
import { useSubmitLeads } from "@src/apis/shared/mutation/use-post-leads";

import ModalSuccess from "@src/components/molecules/modal-success";
import ModalLeads from "@src/components/molecules/modal-leads";
// Components
import CardProperty from "@src/components/atoms/card-property"
import messageAgent from "@src/helper/whatsappLeads";

const DummyList = new Array(30).fill(null);

const LoaderSkeleton = () => {
  return (
    <>
      {DummyList.map((_, i) => {
        return <SkeletonListListing key={`skeleton-property-location-${i}`} />
      })}
    </>
  )
}

const ListListingPropertyGrid = () => {
  const { fetchNextPage, listListing, isFetchingNextPage, hasNextPage, isLoading } = useListListingCtx()
  const { companySlug } = useParams()
  const [selectedProperty, setSelectedProperty] = useState(null)

  const data = useMemo(() => {
    if (!listListing?.pages?.length) return []

    return listListing.pages.map(d => d?.data?.data ?? []).flat()
  }, [listListing])

  const observer = useRef();
  const lastItemRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const { setShowLoader, custProfile } = useGlobalCtx()
  const { mutate } = useSubmitLeads()
  const [openModal, setOpenModal] = useState("")


  const handleTanyaMarketing = (list) => {
    setOpenModal("leads")
    setSelectedProperty(list)
  }

  const onSuccess = () => {
    setOpenModal("success")
    setSelectedProperty(null)
  }

  const onClose = () => {
    setOpenModal("")
    setSelectedProperty(null)
  }

  const onSubmit = (form) => {
    setShowLoader(true)

    let payload = {
      name: form.name,
      phone: "+62" + form.phone.slice(1),
      user_id: selectedProperty?.user?.id,
      property_id: selectedProperty?.id,
      kind: "ONE21 Portal",
      slug: selectedProperty?.slug,
    }

    mutate(payload, {
      onSuccess: () => {
        const domain = window.location.origin + `/office/${companySlug}/detail-listing/${selectedProperty?.slug}`;

        const text = `Hi, saya ingin info lebih lanjut tentang properti ini:\n\n${domain}\n\n_via ONE21 Customer Portal_`
        messageAgent(selectedProperty?.user?.phone, text)
        setOpenModal("success")
      },
      onError: (error) => {
        toast.error(error?.response?.data?.data?.message, "Gagal submit leads")
      },
      onSettled: () => {
        setShowLoader(false)
      }
    })
  }

  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-[40px] 
      dd2-2xl:grid-cols-3 
      dd2-xl:grid-cols-2 dd2-xl:gap-y-4
      dd2-lg:grid-cols-1">
      {!!data?.length && data.map((list, index) => {
        return <CardProperty
          href={`/office/${companySlug}/detail-listing/${list.slug}`}
          key={`list-listing-property-${index}`}
          handleTanyaMarketing={() => handleTanyaMarketing(list)}
          openModal={openModal}
          setOpenModal={setOpenModal}
          onSuccess={onSuccess}
          onClose={onClose}
          onSubmit={onSubmit}
          lastItemRef={lastItemRef}
          {...list}
        />
      })}
      {(isLoading || isFetchingNextPage) && <LoaderSkeleton />}

      <ModalLeads
        isOpen={openModal === "leads"}
        onSuccess={onSuccess}
        onSubmit={onSubmit}
        onClose={onClose}
        defaultName={custProfile?.name ?? ""}
        defaultPhone={custProfile?.phone ?? ""}
      />
      <ModalSuccess
        isOpen={openModal === "success"}
        onSuccess={onSuccess}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </div>
  )
}

export default ListListingPropertyGrid
